import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import ApiService from '../../services/api';

const CONSENT_SCOPES = [
  { id: 'msme:read', label: 'Company profile summary' },
  { id: 'carbon:read', label: 'Carbon score and emissions data' },
  { id: 'reports:read', label: 'Disclosure reports overview' },
  { id: 'transactions:summary', label: 'Transaction summaries (no raw bank data)' },
];

const PartnerConsentSettings: React.FC = () => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ApiService.getMSMEProfile();
      const consent = res?.data?.partnerDataConsent;
      setEnabled(Boolean(consent?.enabled));
    } catch {
      setEnabled(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await ApiService.request('/msme/partner-consent', {
        method: 'PATCH',
        body: JSON.stringify({
          enabled,
          scopes: CONSENT_SCOPES.map((s) => s.id),
        }),
      });
      setMessage(
        enabled
          ? 'Partner data sharing enabled. Banks and accredited partners can access consented summaries via API.'
          : 'Partner data sharing disabled.'
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update consent');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          Partner data sharing
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Allow accredited banks, auditors, and government partners to read verified sustainability
          summaries — and accredited platforms to push consented activity data — when you apply for green finance or respond to buyer audits.
        </Typography>
        <FormControlLabel
          control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
          label="Share consented data with partner integrations"
        />
        <Box component="ul" sx={{ pl: 2.5, mt: 1, mb: 2 }}>
          {CONSENT_SCOPES.map((scope) => (
            <Typography key={scope.id} component="li" variant="body2" color="text.secondary">
              {scope.label}
            </Typography>
          ))}
        </Box>
        {error ? <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert> : null}
        {message ? <Alert severity="success" sx={{ mb: 1 }}>{message}</Alert> : null}
        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save consent preferences'}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default PartnerConsentSettings;
